'use client';

import { Cookie, getOrigin, PageType, useLoyaltyContext, usePageLayout } from '@libs/common-next';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { dataLayerPush, trackError } from '../gtm';
import { getAutoshipSubscriptions, getUserHasActiveSubscriptions, pageDataMapper } from '../helpers';
import Cookies from 'js-cookie';
import { AUTH_COOKIES, COOKIES } from '@libs/core';
import type { EventTracking } from '../types';
import { visitorIdCookie } from 'libs/vercel-edge-rt/src/optimizely';

export const usePageViewTracking = () => {
    const pathname = usePathname();
    const origin = getOrigin();
    const { pageType, userData, user } = usePageLayout();
    const { memberPoints } = useLoyaltyContext();

    const previousPathRef = useRef<string | null>(null);

    const firePageView = async (currentPath: string, prevPath: string | null) => {
        const cartId = Cookies.get(COOKIES.CART_ID);
        const mappedPageData = pageDataMapper(pageType, currentPath);
        const custom_device_id = Cookie.get(visitorIdCookie);

        if (!currentPath.includes('account/order-details')) {
            const pageReferrer = prevPath ? `${origin}${prevPath}` : '';
            const eventData: EventTracking = {
                event: 'page_view',
                page_location: `${origin}${currentPath}`,
                page_title: document.title,
                page_referrer: pageReferrer,
                platform: 'commercetools',
                login_status: Boolean(userData),
                loyalty_id: user?.loyaltyid,
                treats_points: memberPoints?.availablePoints,
                email: userData?.profile?.emailAddress ?? null,
                treats_tier: memberPoints?.currentTierRank ? `tier ${String(memberPoints?.currentTierRank)}` : null,
                cart_id: cartId,
                custom_device_id,
                ...mappedPageData,
            };

            if (userData && user) {
                const responseHasSubscription = await getUserHasActiveSubscriptions(user.sid ?? '');

                eventData.autoship_customer = responseHasSubscription?.hasAutoshipOrders;

                const autoshipSubscriptions = await getAutoshipSubscriptions();
                eventData.first_autoship_eligible = autoshipSubscriptions?.count === 0;
            }

            dataLayerPush(eventData);
        }

        if (mappedPageData.page_type === '404 error') {
            trackError(`${pathname}`, '404');
        }

        if (pageType === PageType.ACP) {
            dataLayerPush({
                event: 'event_accountmanagement',
                pageName: `ps:Account Management${currentPath
                    .split('/')
                    .map(str => str.split('-').join(' '))
                    .join(' ')}`,
                platform: 'commercetools',
            });
        }
    };

    useEffect(() => {
        const hasAccessToken = !!Cookies.get(AUTH_COOKIES.ACCESS_TOKEN);
        if (
            !pathname ||
            (hasAccessToken && !userData && user?.loyaltyid) // await user data if logged
        ) {
            return;
        }

        firePageView(pathname, previousPathRef.current);

        // Update the previousPathRef after firing the event
        previousPathRef.current = pathname;
    }, [pathname, userData, user?.loyaltyid]);
};
